import { ReactElement, ReactNode, useCallback, useEffect, useState } from 'react'

import {
    Carousel,
    CarouselApi,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from '../../components/ui/carousel'
import { cn } from '../../lib/utils'
import { useCmsTranslations } from '../context/RendererContext'

export type CmsCarouselSlide = {
    key: string
    content: ReactNode
}

export type CmsCarouselProps = {
    slides: CmsCarouselSlide[]
    /** Slides visible at the widest breakpoint; one below `sm`, two below `lg`. */
    slidesToShow?: 1 | 2 | 3 | 4
    /** Which slide the carousel opens on. The lightbox needs the one that was clicked. */
    startIndex?: number
    ariaLabel?: string
    className?: string
}

const BASIS: Record<number, string> = {
    1: 'basis-full',
    2: 'basis-full sm:basis-1/2',
    3: 'basis-full sm:basis-1/2 lg:basis-1/3',
    4: 'basis-full sm:basis-1/2 lg:basis-1/4',
}

/**
 * The one carousel in the system, so every block that slides behaves the same way.
 *
 * It snaps, it pages by whole slides, and it says where you are — which is what separates a
 * carousel from a row of cards that merely happens to overflow. The arrows are positioned inside
 * the track rather than the shadcn default of `-left-12`, because a cms block runs to the edge of
 * its container and anything outside it would be clipped.
 */
export const CmsCarousel = ({
    slides,
    slidesToShow = 3,
    startIndex = 0,
    ariaLabel,
    className,
}: CmsCarouselProps): ReactElement | null => {
    const translations = useCmsTranslations()
    const [api, setApi] = useState<CarouselApi>()
    const [selected, setSelected] = useState(0)
    const [pageCount, setPageCount] = useState(0)

    useEffect(() => {
        if (!api) return

        const sync = () => {
            setSelected(api.selectedScrollSnap())
            setPageCount(api.scrollSnapList().length)
        }

        sync()
        api.on('select', sync)
        api.on('reInit', sync)
        return () => {
            api.off('select', sync)
            api.off('reInit', sync)
        }
    }, [api])

    const scrollTo = useCallback((index: number) => api?.scrollTo(index), [api])

    if (slides.length === 0) return null

    return (
        <div className={cn('relative', className)}>
            <Carousel
                setApi={setApi}
                opts={{
                    align: 'start',
                    startIndex,
                    // Paging by the visible count is what makes the arrows advance a screenful
                    // rather than nudging along by one card.
                    slidesToScroll: 'auto',
                    // Looping a two-card carousel duplicates slides and reads as a glitch.
                    loop: slides.length > slidesToShow,
                }}
                className="w-full"
                aria-label={ariaLabel}
            >
                <CarouselContent className="-ml-4">
                    {slides.map((slide) => (
                        <CarouselItem key={slide.key} className={BASIS[slidesToShow]}>
                            {slide.content}
                        </CarouselItem>
                    ))}
                </CarouselContent>

                {/* Hidden when everything already fits: controls that cannot do anything are noise. */}
                {pageCount > 1 && (
                    <>
                        <CarouselPrevious
                            aria-label={translations.carousel.previous}
                            className="left-2 top-1/2 size-10 -translate-y-1/2 bg-white/90 shadow-sm"
                        />
                        <CarouselNext
                            aria-label={translations.carousel.next}
                            className="right-2 top-1/2 size-10 -translate-y-1/2 bg-white/90 shadow-sm"
                        />
                    </>
                )}
            </Carousel>

            {pageCount > 1 && (
                <div className="mt-4 flex justify-center gap-2">
                    {Array.from({ length: pageCount }).map((_, index) => (
                        <button
                            key={index}
                            type="button"
                            onClick={() => scrollTo(index)}
                            aria-label={translations.carousel.goToSlide(index + 1)}
                            aria-current={index === selected}
                            className={cn(
                                'h-1.5 rounded-full transition-all',
                                index === selected
                                    ? 'bg-foreground w-8'
                                    : 'bg-foreground/25 hover:bg-foreground/40 w-4',
                            )}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}
